import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Tag, 
  Space, 
  Typography,
  Card,
  Spin,
  message,
  Popconfirm
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  CheckOutlined, 
  CloseOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { staffService } from '../services/api';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;

interface StaffMember {
  _id: string;
  name: string;
  mobile: string;
  email?: string;
  staffType: 'cleaning' | 'security';
  status: 'active' | 'inactive' | 'suspended';
}

const Staff: React.FC = () => {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState<string | null>(null);
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [emailValue, setEmailValue] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      const response = await staffService.getAll();
      setStaff(response.data.data.staff);
    } catch (error) {
      console.error('Error loading staff:', error);
      message.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      await staffService.create({
        name: values.name,
        mobile: values.mobile,
        password: values.password || '123456',
        staffType: values.staffType,
      });
      message.success('Staff member created successfully');
      setShowModal(false);
      form.resetFields();
      loadStaff();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Error creating staff');
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      setStatusUpdating(id);
      await staffService.updateStatus(id, newStatus);
      message.success('Status updated successfully');
      await loadStaff();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Error updating staff status');
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleEmailUpdate = async (id: string) => {
    if (!emailValue.trim()) {
      message.warning('Please enter an email address');
      return;
    }
    try {
      await staffService.updateEmail(id, emailValue);
      message.success('Email updated successfully');
      await loadStaff();
      setEditingEmail(null);
      setEmailValue('');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Error updating email');
    }
  };

  const columns: ColumnsType<StaffMember> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span className="font-semibold">{text}</span>,
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string, record: StaffMember) => {
        if (editingEmail === record._id) {
          return (
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                placeholder="Enter email"
                autoFocus
                onPressEnter={() => handleEmailUpdate(record._id)}
              />
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => handleEmailUpdate(record._id)}
                style={{ backgroundColor: '#52c41a' }}
              />
              <Button
                icon={<CloseOutlined />}
                onClick={() => {
                  setEditingEmail(null);
                  setEmailValue('');
                }}
              />
            </Space.Compact>
          );
        }
        return (
          <Space>
            <span>{email || 'N/A'}</span>
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setEditingEmail(record._id);
                setEmailValue(email || '');
              }}
            />
          </Space>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'staffType',
      key: 'staffType',
      render: (type: string) => (
        <Tag color={type === 'cleaning' ? 'purple' : 'blue'}>
          {type?.toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: StaffMember) => (
        <Select
          value={status}
          onChange={(value) => handleStatusChange(record._id, value)}
          loading={statusUpdating === record._id}
          style={{ width: 120 }}
          size="small"
        >
          <Select.Option value="active">
            <Tag color="success">Active</Tag>
          </Select.Option>
          <Select.Option value="inactive">
            <Tag color="default">Inactive</Tag>
          </Select.Option>
          <Select.Option value="suspended">
            <Tag color="error">Suspended</Tag>
          </Select.Option>
        </Select>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <Space direction="vertical" size="large" className="w-full">
          <div className="flex justify-between items-center">
            <Title level={2} className="mb-0 flex items-center gap-2">
              <TeamOutlined className="text-blue-600" />
              Staff Management
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setShowModal(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Add Staff
            </Button>
          </div>

          <Card className="shadow-lg border-0 rounded-xl">
            <Table
              columns={columns}
              dataSource={staff}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} staff members`,
              }}
            />
          </Card>

          <Modal
            title={
              <span className="flex items-center gap-2">
                <PlusOutlined />
                Add New Staff
              </span>
            }
            open={showModal}
            onCancel={() => {
              setShowModal(false);
              form.resetFields();
            }}
            footer={null}
            width={600}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="mt-4"
            >
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Please enter staff name' }]}
              >
                <Input placeholder="Enter staff name" size="large" />
              </Form.Item>

              <Form.Item
                label="Mobile Number"
                name="mobile"
                rules={[
                  { required: true, message: 'Please enter mobile number' },
                  { pattern: /^[0-9]{10}$/, message: 'Mobile must be 10 digits' },
                ]}
              >
                <Input
                  placeholder="Enter 10-digit mobile number"
                  maxLength={10}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                tooltip="Leave empty to use default password (123456)"
              >
                <Input.Password
                  placeholder="Default: 123456"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                label="Staff Type"
                name="staffType"
                rules={[{ required: true, message: 'Please select staff type' }]}
                initialValue="cleaning"
              >
                <Select size="large">
                  <Select.Option value="cleaning">
                    <Tag color="purple">Cleaning</Tag>
                  </Select.Option>
                  <Select.Option value="security">
                    <Tag color="blue">Security</Tag>
                  </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item className="mb-0 mt-6">
                <Space className="w-full justify-end">
                  <Button
                    onClick={() => {
                      setShowModal(false);
                      form.resetFields();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" className="bg-blue-600 hover:bg-blue-700">
                    Create Staff
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>
        </Space>
      </div>
    </div>
  );
};

export default Staff;
